import axios, { AxiosError, AxiosResponse } from "axios";
import { type ClassValue, clsx } from "clsx";
import moment from "moment";
import { ChangeEvent } from "react";
import { toast } from "react-toastify";
import { delay, put } from "redux-saga/effects";
import { twMerge } from "tailwind-merge";

import { GroupBase, OptionsOrGroups } from "react-select";
import { LoaderActions } from "../redux/actionTypes/loaderActionTypes";
import { getAwsSignedUrl } from "../redux/sagas/requests/commonRequest";
import store from "../redux/store";
import { ApiResponseType, IDropdownBaseQuery, IDropdownResponse, ISelectlistObj } from "../types/commonInterface";
import { IDropdownItem } from "../types/dropdownInterface";
import { ISiteConfigColors } from "../types/spinnerDatainterface";
import { handleSearch } from "./cancelPreviousRequests";

export function* startLoader(type: string) {
  switch (type) {
    case "page":
      yield put({ type: LoaderActions.SET_PAGE_LOADER, payload: true });
      break;
    case "list":
      yield put({ type: LoaderActions.SET_LIST_LOADER, payload: true });
      break;
    case "transparent":
      yield put({ type: LoaderActions.SET_TRANSPARENT_LOADER, payload: true });
      break;
    case "img_loader":
      yield put({ type: LoaderActions.SET_IMAGE_LOADER, payload: true });
      break;
    case "search":
      yield put({ type: LoaderActions.SET_SEARCH_LOADER, payload: true });
      break;
    default:
      break;
  }
}

export function* stopLoader(type: string) {
  switch (type) {
    case "page":
      yield delay(1000);
      yield put({ type: LoaderActions.SET_PAGE_LOADER, payload: false });
      break;
    case "list":
      yield put({ type: LoaderActions.SET_LIST_LOADER, payload: false });
      break;
    case "transparent":
      yield put({ type: LoaderActions.SET_TRANSPARENT_LOADER, payload: false });
      break;
    case "img_loader":
      yield put({ type: LoaderActions.SET_IMAGE_LOADER, payload: false });
      break;
    case "search":
      yield put({ type: LoaderActions.SET_SEARCH_LOADER, payload: false });
      break;
    default:
      break;
  }
}

export function onKeyDown(keyEvent: any) {
  if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
    keyEvent.preventDefault();
  }
}

export const capitalizeHandler = (name: string, event: ChangeEvent<HTMLInputElement>, formik: any) => {
  const { value } = event.target;
  const finalSentence = value.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
  formik.setFieldValue(name, finalSentence);
};

export const upperCaseHandler = (name: string, event: ChangeEvent<HTMLInputElement>, formik: any) => {
  const { value } = event.target;
  formik.setFieldValue(name, value.toUpperCase());
};

export const convertToDateIsoStringHandler = (name: string, event: ChangeEvent<HTMLInputElement>, formik: any) => {
  const { value } = event.target;
  const isoDateString = moment(value).toISOString();
  formik.setFieldValue(name, isoDateString);
};

export async function copyToClipboard(text: string) {
  try {
    await navigator?.clipboard?.writeText(text);
  } catch (err: any) {
    console.error(`Failed to copy to clipboard! ${err.message}`);
  }
}

export const setTablePageCount = (count: number, pageSize: number) => {
  return Math.ceil(count / pageSize);
};

export const formateDate = (userDate: Date | string, type: string = "MMMM Do YYYY, h:mm a") => {
  const date = moment(userDate);
  const formattedDate = date.format(type);
  return formattedDate;
};

export const getMaxDaysInMonth = (year: number, month: string) => {
  const currentDate = new Date(`${year} ${month}`);
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const date = new Date(currentYear, currentMonth, 1);
  // Set the date to the last day of the month
  date.setMonth(date.getMonth() + 1);
  date.setDate(date.getDate() - 1);

  // Get the day of the month, which will give us the maximum number of days in the month
  return date.getDate();
};

// load dropdown options
export const loadDropdownOptions = (searchValue: string, callback: (options: any[]) => void, api: Function) => {
  if (searchValue) {
    let abortController = handleSearch();
    setTimeout(() => {
      api({ page: 0, search: searchValue, signal: abortController.signal })
        .then((response: AxiosResponse) => callback(response?.data?.data?.rows))
        .catch((error: AxiosError) => console.log(error.message));
    }, 500);
  } else {
    api({ page: 0 })
      .then((response: AxiosResponse) => callback(response?.data?.data?.rows))
      .catch((error: AxiosError) => console.log(error.message));
  }
};
// load dropdown options
export const genericLoadDropdownOptions = <T>(
  searchObject: T,
  callback: (options: any[]) => void,
  api: <R>(data: T) => Promise<AxiosResponse<R, any>>
) => {
  let abortController = handleSearch();
  setTimeout(() => {
    api({ ...searchObject, page: 0, signal: abortController.signal })
      .then((response: AxiosResponse) => callback(response?.data?.data?.rows))
      .catch((error: AxiosError) => console.log(error.message));
  }, 500);
};

export const loadOptions = (
  searchValue: string,
  callback: (options: any[]) => void,
  dropdownApi: Function,
  dropdownDispatch: Function,
  dropdownType: string
) => {
  let options: IDropdownItem[];

  if (searchValue) {
    let abortController = handleSearch();
    setTimeout(() => {
      dropdownApi({ page: 0, search: searchValue, signal: abortController.signal })
        .then((response: AxiosResponse) => {
          options = response?.data?.data?.rows;
          callback(options);
          // dropdownDispatch({ type: dropdownType, payload: options });
        })
        .catch((error: any) => {
          if (error.name === "CanceledError" || error.name === "AbortError") {
            // Ignore aborted request
            return;
          }
          console.error("Dropdown API error:", error);
          callback([]); // fallback empty list to avoid UI break
        });
    }, 500);
  } else {
    dropdownApi({ page: 0 })
      .then((response: AxiosResponse) => {
        options = response?.data?.data?.rows;
        callback(options);
        dropdownDispatch({ type: dropdownType, payload: options });
      })
      .catch((error: any) => {
        console.error("Dropdown API error:", error);
        callback([]); // fallback
      });
  }
};



export const loadAsyncDropdownOptions = async <T>(
  searchValue: string,
  _options: OptionsOrGroups<unknown, GroupBase<unknown>>,
  dropdownApi: (payload: IDropdownBaseQuery) => Promise<AxiosResponse<ApiResponseType<IDropdownResponse>>>,
  currentPage: number,
  params?: T,
) => {
  try {
    const { data } = await dropdownApi({
      page: currentPage,
      pageSize: 10,
      search: searchValue,
      ...params
    });

    return {
      options: data.data.rows,
      hasMore: Math.ceil(data.data.count / 10) > currentPage,
      additional: {
        page: currentPage + 1,
      },
    };

  } catch (error) {
    console.log("Error Fetching Options: ", error);
    return {
      options: [] as ISelectlistObj[],
      hasMore: false,
      additional: {
        page: 0,
      },
    };
  }
};


export const convertToCurrency = (number: number, currency: string) => {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency }).format(number);
};

export const getAwsBucketUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_AWS_BUCKET_URL ?? "";
  return baseUrl.concat(path);
};

export const debounce = (callbackFn: Function, delay = 500) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => callbackFn.apply(null, args), delay);
  };
};

export const apiRequestHandler = async <T>(request: (payload: T) => Promise<AxiosResponse<any, any>>, payload: T) => {
  try {
    store.reduxStore.dispatch({ type: LoaderActions.SET_TRANSPARENT_LOADER, payload: true });
    const { data } = await request(payload);
    return data;
  } catch (error: any) {
    console.error(error);
    toast.error(error?.response?.data?.message ?? "Something went wrong!", {
      position: "top-right",
    });
  } finally {
    store.reduxStore.dispatch({ type: LoaderActions.SET_TRANSPARENT_LOADER, payload: false });
  }
};

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

interface IHandleBlobFileBase {
  responseBlob: Blob | MediaSource;
  behaviour: "download" | "view";
}

type IHandleBlobFile = IHandleBlobFileBase & ({ behaviour: "download"; fileName: string } | { behaviour: "view" });

/**
 * Handles a file from a Blob object by creating a temporary URL and triggering a download or opening the file in a new tab based on the specified behavior.
 * @param params - An object containing the responseBlob, behavior, and fileName properties.
 */
export const handleFileFromBlob = (params: IHandleBlobFile) => {
  const { responseBlob, behaviour } = params;
  const url = URL.createObjectURL(responseBlob);
  const a = document.createElement("a");
  a.href = url;

  if (behaviour === "download") {
    a.download = params.fileName;
  }

  if (behaviour === "view") {
    a.target = "_blank";
  }

  a.click();
  URL.revokeObjectURL(url);
};

export async function getAwsSignedUrlHandler(
  path: string,
  behaviour: "download" | "view"
): Promise<boolean> {
  try {
    const data = await apiRequestHandler(getAwsSignedUrl, { path });
    if (data.status) {
      const signedUrl = data.data;
      if (behaviour === "view") {
        window.open(signedUrl, "_blank"); // open in new tab
        return true;
      } else {
        const response = await axios.get(signedUrl, { responseType: "blob" });
        if (response.status === 200) {
          const fileName = path.split("/").pop() || "file.pdf";
          handleFileFromBlob({ responseBlob: response.data, fileName, behaviour });
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    if (error instanceof AxiosError) {
      toast.error("Error: Unable To Open File, Try Uploading The File Again");
    }
    console.log(error);
    return false;
  }
}


/**
 * Inserts the root styles for the site configuration colors.
 * @param {ISiteConfigColors} siteConfigColorObj - The object containing the site configuration colors.
 */
export const insertSiteConfigRootStyles = (siteConfigColorObj: ISiteConfigColors) => {
  // Find the head element in the document
  const head = document.querySelector("head");

  // Find the style tag with data-emotion attribute set to 'css'
  const dataEmotionStyleTag = document.querySelector("[data-emotion='css']");

  // Check if the head element exists and if the site-config styles are not already inserted
  if (head && !head.textContent?.includes("site-config")) {
    // Create a new style tag
    const styleTag = document.createElement("style");
    styleTag.dataset.styles = "site-config";
    styleTag.innerHTML = `
      :root {
        --primary-default: ${siteConfigColorObj?.primary_default};
        --primary-dark: ${siteConfigColorObj?.primary_dark};
        --primary-medium: ${siteConfigColorObj?.primary_medium};
        --primary-light: ${siteConfigColorObj?.primary_light};
      }
    `;

    // Insert the style tag before the data-emotion style tag
    head.insertBefore(styleTag, dataEmotionStyleTag);
  }
};

/**
 * Appends additional fields to an existing payload object.
 *
 * @template K - The type of the existing payload object.
 * @template T - The type of the additional payload object.
 * @param {K} payload - The existing payload object.
 * @param {T} additionalPayload - The additional payload object to be appended.
 * @returns {K & T} - The final payload object with the additional fields appended.
 */
export const appendFieldsInPayload = <K extends {}, T extends {}>(payload: K, additionalPayload: T): K & T => {
  const finalPayload = {
    ...payload,
    ...additionalPayload,
  };
  return finalPayload;
};
type KeysOf<T> = Extract<keyof T, string>;

export function enumFromKeys<T extends {}>(obj: T): { [K in KeysOf<T>]: K } {
  const keys = Object.keys(obj) as KeysOf<T>[];
  const enumObj: any = {};
  keys.forEach((key) => {
    enumObj[key] = key;
  });
  return enumObj as { [K in KeysOf<T>]: K };
}